import { useEffect, useRef, useState } from "react";
import { ApiError } from "src/services/HttpUtils";

export interface AsyncResourceState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  errorCode: string | null;
  errorStatus: number | null;
}

const initialState = <T,>(loading: boolean): AsyncResourceState<T> => ({
  data: null,
  loading,
  error: null,
  errorCode: null,
  errorStatus: null,
});

const toErrorCode = (reason: unknown): string | null =>
  reason instanceof ApiError && reason.code ? reason.code : null;

const toErrorStatus = (reason: unknown): number | null =>
  reason instanceof ApiError && typeof reason.status === "number" ? reason.status : null;

const toErrorMessage = (reason: unknown, fallback: string): string =>
  reason instanceof Error && reason.message ? reason.message : fallback;


export function useAsyncResource<T>(
  request: (() => Promise<T>) | null,
  fallbackErrorMessage: string
): AsyncResourceState<T> {
  const [state, setState] = useState<AsyncResourceState<T>>(() =>
    initialState<T>(Boolean(request))
  );

  const fallbackRef = useRef(fallbackErrorMessage);
  fallbackRef.current = fallbackErrorMessage;

  useEffect(() => {
    if (!request) {
      setState(initialState<T>(false));
      return;
    }

    let active = true;
    setState(current => ({
      ...current,
      loading: true,
      error: null,
      errorCode: null,
      errorStatus: null,
    }));

    request()
      .then(result => {
        if (active) {
          setState({
            data: result,
            loading: false,
            error: null,
            errorCode: null,
            errorStatus: null,
          });
        }
      })
      .catch((reason: unknown) => {
        if (active) {
          setState({
            data: null,
            loading: false,
            error: toErrorMessage(reason, fallbackRef.current),
            errorCode: toErrorCode(reason),
            errorStatus: toErrorStatus(reason),
          });
        }
      });

    return () => {
      active = false;
    };
  }, [request]);

  return state;
}
